import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Users,
  UserPlus,
  UserCheck,
  UserX,
  Search,
  Clock,
  Check,
  X,
  Dog,
  Loader2,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Friends = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [dogFriendRequests, setDogFriendRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [friendsRes, incomingRes, outgoingRes, dogReqRes] = await Promise.all([
        axios.get(`${API}/friends`),
        axios.get(`${API}/friends/requests/incoming`),
        axios.get(`${API}/friends/requests/outgoing`),
        axios.get(`${API}/dogs/friends/requests/incoming`)
      ]);
      setFriends(friendsRes.data);
      setIncomingRequests(incomingRes.data);
      setOutgoingRequests(outgoingRes.data);
      setDogFriendRequests(dogReqRes.data);
    } catch (error) {
      console.error('Error fetching friends data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/requests/${requestId}/accept`);
      toast.success('Friend request accepted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/requests/${requestId}/decline`);
      toast.success('Request declined');
      fetchData();
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/friends/requests/${requestId}`);
      toast.success('Request cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!window.confirm('Are you sure you want to remove this friend?')) return;
    try {
      await axios.delete(`${API}/friends/${friendId}`);
      toast.success('Friend removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  const handleAcceptDogRequest = async (requestId) => {
    try {
      await axios.post(`${API}/dogs/friends/requests/${requestId}/accept`);
      toast.success('Dog friend request accepted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleDeclineDogRequest = async (requestId) => {
    try {
      await axios.post(`${API}/dogs/friends/requests/${requestId}/decline`);
      toast.success('Request declined');
      fetchData();
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  const filteredFriends = friends.filter(f => 
    f.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    f.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPending = incomingRequests.length + dogFriendRequests.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              Friends
            </h1>
            <p className="text-muted-foreground">Manage your connections and friend requests</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="friends" className="flex items-center gap-2">
              <UserCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Friends</span>
              <Badge variant="secondary" className="ml-1">{friends.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2 relative">
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Requests</span>
              {totalPending > 0 && (
                <Badge className="ml-1 bg-secondary">{totalPending}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Sent</span>
              {outgoingRequests.length > 0 && (
                <Badge variant="secondary" className="ml-1">{outgoingRequests.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="dogs" className="flex items-center gap-2">
              <Dog className="w-4 h-4" />
              <span className="hidden sm:inline">Dog Friends</span>
            </TabsTrigger>
          </TabsList>

          {/* My Friends */}
          <TabsContent value="friends" className="space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search friends..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="friends-search"
              />
            </div>

            {filteredFriends.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Users className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">
                    {searchQuery ? 'No friends found' : 'No friends yet'}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery 
                      ? 'Try a different search term'
                      : 'Start connecting with other dog lovers in the community!'
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredFriends.map((friend) => (
                  <Card key={friend.id} className="card-interactive" data-testid={`friend-card-${friend.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <Avatar 
                          className="w-14 h-14 cursor-pointer"
                          onClick={() => navigate(`/profile/${friend.username}`)}
                        >
                          <AvatarImage src={friend.avatar_url} />
                          <AvatarFallback>{friend.display_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 
                            className="font-medium truncate cursor-pointer hover:text-primary"
                            onClick={() => navigate(`/profile/${friend.username}`)}
                          >
                            {friend.display_name}
                          </h3>
                          <p className="text-sm text-muted-foreground">@{friend.username}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Dog className="w-3 h-3" />
                              {friend.dogs_count} dogs
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDistanceToNow(new Date(friend.friendship_since), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => navigate(`/messages?to=${friend.id}`)}
                        >
                          Message
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveFriend(friend.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <UserX className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Incoming Requests */}
          <TabsContent value="requests" className="space-y-6">
            {/* Owner Friend Requests */}
            <div>
              <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Friend Requests
                {incomingRequests.length > 0 && (
                  <Badge>{incomingRequests.length}</Badge>
                )}
              </h3>
              
              {incomingRequests.length === 0 ? (
                <Card className="text-center py-8">
                  <CardContent>
                    <p className="text-muted-foreground">No pending friend requests</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {incomingRequests.map((request) => (
                    <Card key={request.id} data-testid={`friend-request-${request.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <Avatar 
                            className="w-12 h-12 cursor-pointer"
                            onClick={() => navigate(`/profile/${request.from_username}`)}
                          >
                            <AvatarImage src={request.from_avatar_url} />
                            <AvatarFallback>{request.from_display_name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{request.from_display_name}</p>
                            <p className="text-sm text-muted-foreground">@{request.from_username}</p>
                            {request.message && (
                              <p className="text-sm mt-1 italic">"{request.message}"</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleAcceptRequest(request.id)}
                              className="btn-primary"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeclineRequest(request.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Dog Friend Requests */}
            <div>
              <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
                <Dog className="w-5 h-5" />
                Dog Friend Requests
                {dogFriendRequests.length > 0 && (
                  <Badge>{dogFriendRequests.length}</Badge>
                )}
              </h3>
              
              {dogFriendRequests.length === 0 ? (
                <Card className="text-center py-8">
                  <CardContent>
                    <p className="text-muted-foreground">No pending dog friend requests</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {dogFriendRequests.map((request) => (
                    <Card key={request.id} data-testid={`dog-request-${request.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="flex -space-x-2">
                            <Avatar className="w-10 h-10 border-2 border-background">
                              <AvatarImage src={request.from_dog_image_url} />
                              <AvatarFallback><Dog className="w-4 h-4" /></AvatarFallback>
                            </Avatar>
                            <Avatar className="w-10 h-10 border-2 border-background">
                              <AvatarImage src={request.to_dog_image_url} />
                              <AvatarFallback><Dog className="w-4 h-4" /></AvatarFallback>
                            </Avatar>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">
                              <span className="text-primary">{request.from_dog_name}</span>
                              {' wants to be friends with '}
                              <span className="text-secondary">{request.to_dog_name}</span>
                            </p>
                            <p className="text-sm text-muted-foreground">
                              From @{request.from_owner_username}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleAcceptDogRequest(request.id)}
                              className="btn-primary"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeclineDogRequest(request.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Sent Requests */}
          <TabsContent value="sent" className="space-y-4">
            {outgoingRequests.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Send className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">No pending requests</h3>
                  <p className="text-muted-foreground">
                    Friend requests you send will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {outgoingRequests.map((request) => (
                  <Card key={request.id} data-testid={`outgoing-request-${request.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <Avatar 
                          className="w-12 h-12 cursor-pointer"
                          onClick={() => navigate(`/profile/${request.to_username}`)}
                        >
                          <AvatarImage src={request.to_avatar_url} />
                          <AvatarFallback>{request.to_display_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{request.to_display_name}</p>
                          <p className="text-sm text-muted-foreground">@{request.to_username}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            Sent {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleCancelRequest(request.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          Cancel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Dog Friends Tab */}
          <TabsContent value="dogs" className="space-y-4">
            <Card className="text-center py-12">
              <CardContent>
                <Dog className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-xl mb-2">Dog Friends</h3>
                <p className="text-muted-foreground mb-4">
                  View and manage your dogs' friendships from their individual profiles
                </p>
                <Button onClick={() => navigate('/catalog')} className="btn-primary">
                  Go to My Dogs
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Friends;
